import { useEffect, useState } from "react";
import { Crown, Loader2, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

function Tile({ label, value, testId }) {
  return (
    <div className="bg-white rounded-xl border border-app-border px-4 py-3" data-testid={testId}>
      <div className="text-2xl font-heading font-semibold text-espresso leading-none">{value}</div>
      <div className="text-[11px] text-espresso/60 mt-1">{label}</div>
    </div>
  );
}

const MEDAL = ["text-amber-500", "text-slate-400", "text-amber-700"];

export const ReferralLeaderboard = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/referrals/leaderboard").then(({ data: d }) => setData(d)).catch(() => setData({ rows: [], totals: {} }));
  }, []);

  return (
    <section className="space-y-3" data-testid="referral-leaderboard">
      <div>
        <h2 className="font-heading text-xl font-semibold text-espresso flex items-center gap-2"><Trophy className="w-5 h-5 text-olive" />{t("lb.title")}</h2>
        <p className="text-xs text-espresso/60">{t("lb.sub")}</p>
      </div>
      {data === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Tile label={t("lb.inviters")} value={data.totals.inviters ?? 0} testId="lb-inviters" />
            <Tile label={t("lb.signups")} value={data.totals.signups ?? 0} testId="lb-signups" />
            <Tile label={t("lb.subscribed")} value={data.totals.subscribed ?? 0} testId="lb-subscribed" />
            <Tile label={t("lb.days_given")} value={data.totals.days_given ?? 0} testId="lb-days" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="lb-current-leader">
            <Trophy className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{t("lb.leader_title")} · {data.current_month?.month}</div>
              {data.current_month?.leader ? (
                <div className="text-espresso">
                  <span className="font-semibold">{data.current_month.leader.name || data.current_month.leader.email}</span>
                  {" · "}{data.current_month.leader.signups} {t("lb.col_signups").toLowerCase()}
                </div>
              ) : (
                <div className="text-espresso/60">{t("lb.no_leader")}</div>
              )}
              <div className="text-[11px] text-espresso/60">{t("lb.bonus_note").replace("{d}", data.bonus_days ?? 1)}</div>
            </div>
            {data.past_winners?.length > 0 && (
              <ul className="text-[11px] text-espresso/70 space-y-0.5" data-testid="lb-past-winners">
                {data.past_winners.slice(0, 3).map((w) => (
                  <li key={w.month}><span className="font-mono">{w.month}</span> · {w.name || w.email} (+{w.bonus_days})</li>
                ))}
              </ul>
            )}
          </div>
          {data.rows.length === 0 ? (
            <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-4" data-testid="lb-empty">{t("lb.empty")}</div>
          ) : (
            <div className="bg-white rounded-xl border border-app-border overflow-hidden" data-testid="lb-rows">
              <table className="w-full text-sm">
                <thead className="bg-cream-2 text-[11px] uppercase tracking-wider text-espresso/60">
                  <tr>
                    <th className="text-start px-3 py-2">#</th>
                    <th className="text-start px-3 py-2">{t("lb.breeder")}</th>
                    <th className="text-end px-3 py-2">{t("lb.col_signups")}</th>
                    <th className="text-end px-3 py-2">{t("lb.col_subscribed")}</th>
                    <th className="text-end px-3 py-2">{t("lb.col_days")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-app-border">
                  {data.rows.map((r, i) => (
                    <tr key={r.user_id} data-testid={`lb-row-${i}`}>
                      <td className="px-3 py-2 font-bold text-espresso/70">
                        {i < 3 ? <Crown className={`w-4 h-4 ${MEDAL[i]}`} /> : i + 1}
                      </td>
                      <td className="px-3 py-2 min-w-0">
                        <div className="font-semibold text-espresso truncate">{r.name || r.email}</div>
                        <div className="text-[11px] text-espresso/50 truncate">{r.email}{r.code ? ` · ${r.code}` : ""}</div>
                      </td>
                      <td className="px-3 py-2 text-end font-semibold">{r.signups}</td>
                      <td className="px-3 py-2 text-end">{r.subscribed}</td>
                      <td className="px-3 py-2 text-end text-olive font-bold">+{r.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};
